import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import RequestCard from '../components/RequestCard';
import KompensasiForm from '../components/forms/KompensasiForm';
import KersosForm from '../components/forms/KersosForm';
import PSMForm from '../components/forms/PSMForm';
import { authFetch } from '../services/authFetch';

const ReportPage = () => {
  const location = useLocation();
  const [activeForm, setActiveForm] = useState(null);
  const [viewingHistoricalRequest, setViewingHistoricalRequest] = useState(null);
  const [editingRequest, setEditingRequest] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ show: false, report: null });
  const [operationLoading, setOperationLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  
  // Check if we're navigating from the activity view
  useEffect(() => {
    if (location.state && location.state.viewingHistoricalRequest) {
      setViewingHistoricalRequest(location.state.viewingHistoricalRequest);
    }
  }, [location]);

  // Fetch all report data from API
  useEffect(() => {
    const fetchReports = async () => {
      setLoading(true);
      setError(null);
      try {
        
        // Fetch all report types in parallel
        const [overtimeRes, kersosRes, psmRes] = await Promise.all([
          authFetch('/api/overtime_reports'),
          authFetch('/api/kersos_reports'),
          authFetch('/api/psm_reports')
        ]);
        
        const [overtime, kersos, psm] = await Promise.all([
          overtimeRes.json(),
          kersosRes.json(),
          psmRes.json()
        ]);

        // Map and merge all reports into a single array for the table
        const mappedOvertime = (Array.isArray(overtime) ? overtime : []).map(item => {
          // Map backend status to UI status
          let status = 'pending';
          let statusText = 'Menunggu';
          if (item.request_status === 'disetujui' || item.request_status === 'approved') {
            status = 'approved';
            statusText = 'Disetujui';
          } else if (item.request_status === 'ditolak' || item.request_status === 'rejected') {
            status = 'rejected';
            statusText = 'Ditolak';
          } else if (item.request_status === 'menunggu' || item.request_status === 'pending') {
            status = 'pending';
            statusText = 'Menunggu';
          }
          
          // Format date using Intl.DateTimeFormat
          let formattedDate = '';
          if (item.created_at) {
            const dateObj = new Date(item.created_at);
            formattedDate = new Intl.DateTimeFormat('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }).format(dateObj);
          }
          
          return {
            id: `overtime-${item.id}`,
            type: 'Lembur',
            date: formattedDate,
            status,
            statusText,
            formType: 'kompensasi',
            data: {
              activityDate: item.activity_date || '',
              duration: item.duration || '',
              activityDesc: item.activity_description || '',
              location: item.location || '',
              supervisor: item.supervisor || '',
              supervisorPosition: item.supervisor_position || '',
              phoneNumber: item.phone_number || '',
              ...item // keep all original fields for fallback
            }
          };
        });
        
        const mappedKersos = (Array.isArray(kersos) ? kersos : []).map(item => {
          // Map backend status to UI status
          let status = 'pending';
          let statusText = 'Menunggu';
          if (item.request_status === 'disetujui' || item.request_status === 'approved') {
            status = 'approved';
            statusText = 'Disetujui';
          } else if (item.request_status === 'ditolak' || item.request_status === 'rejected') {
            status = 'rejected';
            statusText = 'Ditolak';
          } else if (item.request_status === 'menunggu' || item.request_status === 'pending') {
            status = 'pending';
            statusText = 'Menunggu';
          }
          
          // Format date using Intl.DateTimeFormat
          let formattedDate = '';
          if (item.created_at) {
            const dateObj = new Date(item.created_at);
            formattedDate = new Intl.DateTimeFormat('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }).format(dateObj);
          }
          
          return {
            id: `kersos-${item.id}`,
            type: 'Kersos',
            date: formattedDate,
            status,
            statusText,
            formType: 'kersos',
            data: {
              activityDate: item.activity_date || '',
              duration: item.duration || '',
              activityDesc: item.activity_description || '',
              location: item.location || '',
              supervisor: item.supervisor || '',
              supervisorPosition: item.supervisor_position || '',
              phoneNumber: item.phone_number || '',
              ...item // keep all original fields for fallback
            }
          };
        });
        
        const mappedPsm = (Array.isArray(psm) ? psm : []).map(item => {
          // Map backend status to UI status
          let status = 'pending';
          let statusText = 'Menunggu';
          if (item.request_status === 'disetujui' || item.request_status === 'approved') {
            status = 'approved';
            statusText = 'Disetujui';
          } else if (item.request_status === 'ditolak' || item.request_status === 'rejected') {
            status = 'rejected';
            statusText = 'Ditolak';
          } else if (item.request_status === 'menunggu' || item.request_status === 'pending') {
            status = 'pending';
            statusText = 'Menunggu';
          }
          
          // Format date using Intl.DateTimeFormat
          let formattedDate = '';
          if (item.created_at) {
            const dateObj = new Date(item.created_at);
            formattedDate = new Intl.DateTimeFormat('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }).format(dateObj);
          }
          
          return {
            id: `psm-${item.id}`,
            type: 'PSM',
            date: formattedDate,
            status,
            statusText,
            formType: 'psm',
            data: {
              activityDate: item.activity_date || '',
              duration: item.duration || '',
              activityDesc: item.activity_description || '',
              location: item.location || '',
              supervisor: item.supervisor || '',
              supervisorPosition: item.supervisor_position || '',
              phoneNumber: item.phone_number || '',
              ...item // keep all original fields for fallback
            }
          };
        });
        
        // Combine all reports and sort by date (newest first)
        const allReports = [...mappedOvertime, ...mappedKersos, ...mappedPsm];
        allReports.sort((a, b) => {
          return new Date(b.data.created_at || 0) - new Date(a.data.created_at || 0);
        });
        
        setReports(allReports);
      } catch (err) {
        console.error('Error fetching reports:', err);
        setError('Gagal memuat data laporan. Silakan coba lagi nanti.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchReports();
  }, []);
  
  const reportTypes = [
    // {
    //   id: 'kompensasi',
    //   title: 'Lembur',
    //   description: 'Laporan kegiatan lembur siswa untuk pekerjaan tambahan',
    //   icon: '🔄',
    //   color: '#e8f1fd'
    // },
    {
      id: 'kersos',
      title: 'Kersos',
      description: 'Laporan kegiatan kerja sosial yang telah dilaksanakan',
      icon: '🤝',
      color: '#e6f8f5'
    },
    {
      id: 'psm',
      title: 'PSM',
      description: 'Laporan pengabdian sosial masyarakat yang telah dilakukan',
      icon: '🌟',
      color: '#fff3e0'
    }
  ];

  const handleViewHistoricalRequest = (requestId) => {
    const request = reports.find(req => req.id === requestId);
    if (request) {
      setViewingHistoricalRequest(request);
    }
  };

  const handleEditRequest = (requestId) => {
    const request = reports.find(req => req.id === requestId);
    if (request && request.status === 'pending') {
      setEditingRequest(request);
    } else {
      setNotification({ 
        show: true, 
        message: 'Tidak dapat diubah - Hanya laporan dengan status menunggu yang dapat diedit', 
        type: 'error' 
      });
    }
  };

  const handleDeleteRequest = (requestId) => {
    const request = reports.find(req => req.id === requestId);
    if (request && request.status === 'pending') {
      setDeleteModal({ show: true, report: request });
    } else {
      setNotification({ 
        show: true, 
        message: 'Tidak dapat dihapus - Hanya laporan dengan status menunggu yang dapat dihapus', 
        type: 'error' 
      });
    }
  };

  const confirmDelete = async () => {
    if (!deleteModal.report) return;
    
    setOperationLoading(true);
    try {
      const reportId = deleteModal.report.id.split('-')[1];
      const reportType = deleteModal.report.id.split('-')[0];
      
      let endpoint = '';
      switch (reportType) {
        case 'kersos':
          endpoint = `/api/kersos_reports/${reportId}`;
          break;
        case 'psm':
          endpoint = `/api/psm_reports/${reportId}`;
          break;
        case 'overtime':
          endpoint = `/api/overtime_reports/${reportId}`;
          break;
        default:
          throw new Error('Unknown report type');
      }

      const response = await authFetch(endpoint, {
        method: 'DELETE'
      });

      if (response.ok) {
        setReports(prev => prev.filter(report => report.id !== deleteModal.report.id));
        setNotification({ 
          show: true, 
          message: 'Laporan berhasil dihapus', 
          type: 'success' 
        });
        setDeleteModal({ show: false, report: null });
      } else {
        const data = await response.json();
        setNotification({ 
          show: true, 
          message: data.message || 'Gagal menghapus laporan', 
          type: 'error' 
        });
      }
    } catch (error) {
      setNotification({ 
        show: true, 
        message: 'Terjadi kesalahan saat menghapus laporan', 
        type: 'error' 
      });
    } finally {
      setOperationLoading(false);
      setDeleteModal({ show: false, report: null });
    }
  };

  const handleUpdateReport = async (updatedData) => {
    if (!editingRequest) return;
    
    setOperationLoading(true);
    try {
      const reportId = editingRequest.id.split('-')[1];
      const reportType = editingRequest.id.split('-')[0];
      
      let endpoint = '';
      switch (reportType) {
        case 'kersos':
          endpoint = `/api/kersos_reports/${reportId}`;
          break;
        case 'psm':
          endpoint = `/api/psm_reports/${reportId}`;
          break;
        case 'overtime':
          endpoint = `/api/overtime_reports/${reportId}`;
          break;
        default:
          throw new Error('Unknown report type');
      }
      // Debug: log update intent and payload keys
      try {
        const payloadKeys = Object.keys(updatedData || {});
        console.log('[Update] Endpoint:', endpoint, 'ReportType:', reportType, 'ID:', reportId, 'Keys:', payloadKeys);
      } catch (_) {}
      // Decide payload format: use FormData if there are new image files, otherwise JSON
      const images = Array.isArray(updatedData.images) ? updatedData.images : [];
      const hasNewFiles = images.some(img => (typeof File !== 'undefined' && img instanceof File) || (typeof Blob !== 'undefined' && img instanceof Blob));

      // Compute removed_image_ids by comparing original server images vs retained server images
      const originalServerImages = Array.isArray(editingRequest?.data?.images)
        ? editingRequest.data.images.filter(img => img && typeof img === 'object' && 'id' in img)
        : [];
      const originalIdsSet = new Set(originalServerImages.map(img => img.id));
      const retainedServerIdsSet = new Set(
        images
          .filter(img => img && typeof img === 'object' && !(typeof File !== 'undefined' && img instanceof File) && !(typeof Blob !== 'undefined' && img instanceof Blob))
          .map(img => img.id)
          .filter(id => typeof id === 'number' || typeof id === 'string')
      );
      const removedImageIds = [...originalIdsSet].filter(id => !retainedServerIdsSet.has(id));

      // Detect if user intends to keep some existing images while adding new ones
      const hasRetainedExistingImages = retainedServerIdsSet.size > 0;

      let response;
      if (hasNewFiles) {
        const fd = new FormData();
        // Append known fields if present
        const fields = [
          // PSM
          'psm_date','start_time','finish_time','apresiasis_id','location','description','reflection','pic','pic_contact_number','admin_note','activity_date',
          // Kersos
          'kersos_date','type_or_work'
        ];
        fields.forEach((key) => {
          if (updatedData[key] !== undefined && updatedData[key] !== null) {
            fd.append(key, updatedData[key]);
          }
        });
        // Append only File objects; skip existing server images (strings or objects)
        images.forEach((img) => {
          if (typeof File !== 'undefined' && img instanceof File) {
            fd.append('images[]', img, img.name);
          } else if (typeof Blob !== 'undefined' && img instanceof Blob) {
            const fileName = `image_${Date.now()}.jpg`;
            const fileFromBlob = new File([img], fileName, { type: img.type || 'image/jpeg', lastModified: Date.now() });
            fd.append('images[]', fileFromBlob, fileFromBlob.name);
          }
        });
        // Include specific removals if any
        if (removedImageIds && removedImageIds.length > 0) {
          removedImageIds.forEach((id) => fd.append('removed_image_ids[]', id));
        }
        // If user kept some existing images, do not replace all; otherwise let backend default behavior apply
        if (hasRetainedExistingImages) {
          fd.append('replace_images', 'false');
        }
        // Laravel/PHP typically requires POST with _method override for file uploads
        fd.append('_method', 'PUT');
        response = await authFetch(endpoint, {
          method: 'POST',
          body: fd,
          headers: { /* let browser set multipart boundary */ }
        });
        // Fallback: some APIs accept PUT with multipart directly
        if (!response.ok && (response.status === 404 || response.status === 405)) {
          response = await authFetch(endpoint, {
            method: 'PUT',
            body: fd,
            headers: { /* let browser set multipart boundary */ }
          });
        }
      } else {
        // Clean JSON payload: avoid sending images key to API unless needed
        const { images: _omitImages, ...cleanData } = updatedData || {};
        // Attach removed_image_ids for JSON updates when user removed existing images
        if (removedImageIds && removedImageIds.length > 0) {
          cleanData.removed_image_ids = removedImageIds;
        }
        response = await authFetch(endpoint, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(cleanData)
        });
      }

      if (response.ok) {
        console.log('[Update] Response OK:', response.status, response.statusText);
        // Some APIs return 204 No Content for successful PUT; safely parse body if present
        let updatedReport = {};
        try {
          const contentType = response.headers.get('content-type') || '';
          if (response.status === 204) {
            updatedReport = {};
          } else if (contentType.includes('application/json')) {
            updatedReport = await response.json();
          } else {
            const txt = await response.text();
            updatedReport = txt ? JSON.parse(txt) : {};
          }
        } catch (_) {
          // Fallback to empty object if body is empty or not JSON
          updatedReport = {};
        }

        // Always fetch fresh record to guarantee UI sync with DB
        let freshReport = null;
        try {
          const refRes = await authFetch(endpoint, { method: 'GET' });
          if (refRes.ok) {
            const ct = refRes.headers.get('content-type') || '';
            if (ct.includes('application/json')) {
              freshReport = await refRes.json();
              console.log('[Update] Fetched fresh record after update:', freshReport);
            }
          } else {
            console.warn('[Update] Fresh fetch failed:', refRes.status, await refRes.text());
          }
        } catch (freshErr) {
          console.warn('[Update] Fresh fetch error:', freshErr);
          freshReport = null;
        }
        const mergedReport = freshReport || updatedReport || {};

        setReports(prev => prev.map(report => 
          report.id === editingRequest.id 
            ? { 
                ...report, 
                // Avoid merging File objects into local data state
                data: { 
                  ...report.data, 
                  ...(Array.isArray(updatedData.images) ? (() => { const { images: _imgs, ...rest } = updatedData; return rest; })() : updatedData),
                  ...(mergedReport && mergedReport.images ? { images: mergedReport.images } : {}),
                  ...(mergedReport && mergedReport.psm_images ? { images: mergedReport.psm_images } : {}),
                  ...(mergedReport && mergedReport.image_paths ? { images: mergedReport.image_paths } : {})
                },
                date: new Intl.DateTimeFormat('id-ID', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                }).format(new Date(
                  updatedData.psm_date || mergedReport.psm_date || updatedData.activity_date || mergedReport.activity_date || report.date
                ))
              }
            : report
        ));
        setNotification({ 
          show: true, 
          message: `Laporan berhasil diperbarui (status ${response.status})`, 
          type: 'success' 
        });
        setEditingRequest(null);
      } else {
        // Try to read error payload safely
        const errorText = await response.text();
        let errorJson = null;
        try { errorJson = JSON.parse(errorText); } catch (_) { /* ignore */ }
        setNotification({ 
          show: true, 
          message: (errorJson && errorJson.message) ? errorJson.message : (errorText || `Gagal memperbarui laporan (status ${response.status})`), 
          type: 'error' 
        });
        console.warn('[Update] Response not OK:', response.status, errorJson || errorText);
      }
    } catch (error) {
      setNotification({ 
        show: true, 
        message: error.message || 'Terjadi kesalahan saat memperbarui laporan', 
        type: 'error' 
      });
      console.error('[Update] Exception:', error);
    } finally {
      setOperationLoading(false);
    }
  };
  
  const renderForm = () => {
    // If editing a request
    if (editingRequest) {
      const request = editingRequest;
      
      // Show the appropriate form with prefilled data in edit mode
      switch(request.formType) {
        case 'kompensasi':
          return (
            <KompensasiForm 
              onClose={() => setEditingRequest(null)} 
              initialData={request.data}
              readOnly={false}
              onSubmit={handleUpdateReport}
              isEdit={true}
            />
          );
        case 'kersos':
          return (
            <KersosForm 
              onClose={() => setEditingRequest(null)} 
              initialData={request.data}
              readOnly={false}
              onSubmit={handleUpdateReport}
              isEdit={true}
            />
          );
        case 'psm':
          return (
            <PSMForm 
              onClose={() => setEditingRequest(null)} 
              initialData={request.data}
              readOnly={false}
              onSubmit={handleUpdateReport}
              isEdit={true}
            />
          );
        default:
          return null;
      }
    }
    
    // If viewing a historical request
    if (viewingHistoricalRequest) {
      const request = viewingHistoricalRequest;
      
      // Show the appropriate form with prefilled data and read-only
      switch(request.formType) {
        case 'kompensasi':
          return (
            <KompensasiForm 
              onClose={() => setViewingHistoricalRequest(null)} 
              initialData={request.data}
              readOnly={true}
              status={request.status}
              statusText={request.statusText}
            />
          );
        case 'kersos':
          return (
            <KersosForm 
              onClose={() => setViewingHistoricalRequest(null)} 
              initialData={request.data}
              readOnly={true}
              status={request.status}
              statusText={request.statusText}
            />
          );
        case 'psm':
          return (
            <PSMForm 
              onClose={() => setViewingHistoricalRequest(null)} 
              initialData={request.data}
              readOnly={true}
              status={request.status}
              statusText={request.statusText}
            />
          );
        default:
          return null;
      }
    }
    
    // If creating a new report
    if (activeForm) {
      switch(activeForm) {
        case 'kompensasi':
          return (
            <KompensasiForm 
              onClose={() => setActiveForm(null)} 
            />
          );
        case 'kersos':
          return (
            <KersosForm 
              onClose={() => setActiveForm(null)} 
            />
          );
        case 'psm':
          return (
            <PSMForm 
              onClose={() => setActiveForm(null)} 
            />
          );
        default:
          return null;
      }
    }
    
    return null;
  };
  
  return (
    <PageContainer>
      <PageHeader>
        <h1>Laporan Kegiatan</h1>
        <p>Buat dan lihat laporan kegiatan yang telah Anda lakukan</p>
      </PageHeader>
      
      {/* Notification Toast */}
      {notification.show && (
        <NotificationToast $type={notification.type}>
          <NotificationMessage>{notification.message}</NotificationMessage>
          <CloseNotificationButton onClick={() => setNotification({ ...notification, show: false })}>
            ×
          </CloseNotificationButton>
        </NotificationToast>
      )}
      
      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <ModalOverlay onClick={() => setDeleteModal({ show: false, report: null })}>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <DeleteConfirmationContent>
              <h3>Konfirmasi Penghapusan</h3>
              <p>Apakah Kamu ingin menghapus laporan {deleteModal.report?.type} ini?</p>
              <p>Tindakan ini tidak dapat dibatalkan.</p>
              <DeleteConfirmationButtons>
                <CancelButton 
                  onClick={() => setDeleteModal({ show: false, report: null })}
                  disabled={operationLoading}
                >
                  Batal
                </CancelButton>
                <ConfirmDeleteButton 
                  onClick={confirmDelete}
                  disabled={operationLoading}
                >
                  {operationLoading ? 'Menghapus...' : 'Hapus'}
                </ConfirmDeleteButton>
              </DeleteConfirmationButtons>
            </DeleteConfirmationContent>
          </ModalContainer>
        </ModalOverlay>
      )}
      
      {/* Form Modals */}
      {(viewingHistoricalRequest || activeForm || editingRequest) && (
        <ModalOverlay 
          onClick={() => {
            setViewingHistoricalRequest(null);
            setActiveForm(null);
            setEditingRequest(null);
          }}
        >
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            {renderForm()}
          </ModalContainer>
        </ModalOverlay>
      )}
      
      {!viewingHistoricalRequest && !editingRequest && (
        <>
          <RequestGrid>
            {reportTypes.map(type => (
              <RequestCard 
                key={type.id}
                title={type.title}
                description={type.description}
                icon={type.icon}
                color={type.color}
                onClick={() => setActiveForm(type.id)}
              />
            ))}
          </RequestGrid>
          
          <RecentRequestsSection>
            <h2>Riwayat Pengajuan</h2>
            {loading ? (
              <LoadingMessage>Memuat data laporan...</LoadingMessage>
            ) : error ? (
              <ErrorMessage>{error}</ErrorMessage>
            ) : reports.length === 0 ? (
              <EmptyMessage>Belum ada laporan yang diajukan.</EmptyMessage>
            ) : (
              <RequestTable>
                <thead>
                  <tr>
                    <th>Jenis Laporan</th>
                    <th>Tanggal Pengajuan</th>
                    <th>Status</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map(report => (
                    <tr key={report.id}>
                      <td>{report.type}</td>
                      <td>{report.date}</td>
                      <td><StatusBadge status={report.status}>{report.statusText}</StatusBadge></td>
                      <td>
                        <ActionButtonsContainer>
                          <ActionButton onClick={() => handleViewHistoricalRequest(report.id)}>Lihat</ActionButton>
                          {report.status === 'pending' && (
                            <>
                              <ActionButton 
                                onClick={() => handleEditRequest(report.id)}
                                disabled={operationLoading}
                              >
                                Edit
                              </ActionButton>
                              <ActionButton 
                                onClick={() => handleDeleteRequest(report.id)}
                                disabled={operationLoading}
                                style={{ backgroundColor: '#dc3545', borderColor: '#dc3545', color:'white'}}
                              >
                                Hapus
                              </ActionButton>
                            </>
                          )}
                        </ActionButtonsContainer>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </RequestTable>
            )}
          </RecentRequestsSection>
        </>
      )}
    </PageContainer>
  );
};

const PageContainer = styled.div`
  padding: 1.5rem;
  max-width: 1280px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
  
  h1 {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--text-dark);
    margin-bottom: 0.5rem;
  }
  
  p {
    color: var(--text-light);
  }
`;

const RequestGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-bottom: 2.5rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const RecentRequestsSection = styled.div`
  background-color: var(--background);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: var(--box-shadow);
  transition: background-color 0.3s, box-shadow 0.3s;
  
  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 1.5rem;
    color: var(--text-dark);
  }
`;

const RequestTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9375rem;
  
  th, td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid var(--border);
  }
  
  th {
    font-weight: 600;
    color: var(--text-dark);
    background-color: var(--background-light);
  }
  
  tr:last-child td {
    border-bottom: none;
  }
  
  @media (max-width: 768px) {
    display: block;
    overflow-x: auto;
    
    th, td {
      padding: 0.75rem;
    }
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 1rem;
  font-size: 0.95rem;
  font-weight: 600;
  
  ${props => {
    switch(props.status) {
      case 'approved':
        return `
          background-color: #e6f8f5;
          color: #00a389;
        `;
      case 'pending':
        return `
          background-color: #fff3e0;
          color: #ff9800;
        `;
      case 'rejected':
        return `
          background-color: #feeeee;
          color: #dc3545;
        `;
      default:
        return '';
    }
  }}
`;

const ActionButton = styled.button`
  background-color: transparent;
  color: var(--primary);
  border: 1px solid var(--primary);
  border-radius: 4px;
  padding: 0.35rem 0.75rem;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: var(--primary);
    color: white;
  }
`;

const LoadingMessage = styled.div`
  padding: 2rem 0;
  text-align: center;
  color: var(--text-light);
  font-size: 1rem;
`;

const ErrorMessage = styled.div`
  padding: 1.5rem;
  background-color: ${props => props.theme === 'dark' ? '#3a1c1c' : '#feeeee'};
  color: ${props => props.theme === 'dark' ? '#ff6b6b' : '#dc3545'};
  border-radius: 8px;
  margin-bottom: 1rem;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  
  &:before {
    content: "⚠️";
    margin-right: 0.75rem;
  }
`;

const EmptyMessage = styled.div`
  padding: 2rem 0;
  text-align: center;
  color: var(--text-light);
  font-style: italic;
  font-size: 0.95rem;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContainer = styled.div`
  background-color: var(--background);
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    max-width: 95vw;
  }
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
`;

// Notification Toast Styles
const NotificationToast = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  background: ${props => props.$type === 'success' ? '#28a745' : '#dc3545'};
  color: white;
  padding: 16px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 300px;
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const NotificationMessage = styled.span`
  flex: 1;
  font-weight: 500;
`;

const CloseNotificationButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  padding: 0;
  margin-left: 10px;
  opacity: 0.8;
  
  &:hover {
    opacity: 1;
  }
`;

// Delete Confirmation Modal Styles
const DeleteConfirmationContent = styled.div`
  text-align: center;
  padding: 20px 0;
`;

const DeleteConfirmationButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 24px;
`;

const CancelButton = styled.button`
  background: #6c757d;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
  
  &:hover:not(:disabled) {
    background: #5a6268;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ConfirmDeleteButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
  
  &:hover:not(:disabled) {
    background: #c82333;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export default ReportPage;
